import { clsx, type ClassValue } from 'clsx'

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

export function formatTime(date: string): string {
  return new Date(date).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Kurzes haptisches Feedback (Android/Chrome). iOS Safari ignoriert es still. */
export function haptic(ms = 10): void {
  try {
    navigator.vibrate?.(ms)
  } catch {
    /* ignore */
  }
}

/** Sicherer sessionStorage-Zugriff (Private-Mode / blockierter Storage wirft). */
export const storage = {
  get(key: string): string | null {
    try {
      return sessionStorage.getItem(key)
    } catch {
      return null
    }
  },
  set(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value)
    } catch {
      /* ignore */
    }
  },
  remove(key: string): void {
    try {
      sessionStorage.removeItem(key)
    } catch {
      /* ignore */
    }
  },
}

// ── A/B Test: variant assignment + persistent session per device ──────────────

export type Variant = 'chat' | 'click'

export function assignVariant(): Variant {
  return Math.random() < 0.5 ? 'chat' : 'click'
}

/**
 * Reads or creates a per-device A/B assignment in sessionStorage.
 * Same browser tab on same device = same variant = same A/B bucket.
 */
export function getOrCreateAbAssignment(deviceId: string): {
  variant: Variant
  isNew: boolean
} {
  const key = `vendi_ab_${deviceId}`
  const stored = storage.get(key)
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      if (parsed.variant === 'chat' || parsed.variant === 'click') {
        return { variant: parsed.variant, isNew: false }
      }
    } catch {
      /* fall through */
    }
  }
  const variant = assignVariant()
  storage.set(key, JSON.stringify({ variant }))
  return { variant, isNew: true }
}

/**
 * Adresse für den Header in Zeilen aufteilen:
 * „Dorfstraße 16, 79280 Au" → ["Dorfstraße 16", "79280 Au"].
 * Ohne Komma wird vor „PLZ Ort" getrennt; sonst bleibt die Adresse einzeilig.
 */
export function formatAddressLines(address: string): string[] {
  const clean = address.replace(/\s+/g, ' ').trim()
  if (!clean) return []
  const parts = clean.split(',').map((p) => p.trim()).filter(Boolean)
  if (parts.length >= 2) {
    const [street, ...rest] = parts
    return [street, rest.join(', ')]
  }
  const m = clean.match(/^(.+?)\s+(\d{4,5}\s+\S.*)$/)
  if (m) return [m[1], m[2]]
  return [clean]
}
